import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypotEl = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypotEl && honeypotEl.value.trim() !== '') {
      setFormStatus('success');
      form.reset();
      return;
    }
    setFormStatus('loading');
    setFormError('');

    try {
      const formData = new FormData(form);
      formData.delete('phone_alt');
      const response = await fetch('https://readdy.ai/api/form/d9rceosnlsngrm4lma6g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText;
        setFormStatus('error');
        setFormError(serverMsg || '提交失败，请稍后再试');
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查网络后重试');
    }
  };

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb items={[{ label: '关于我们' }]} />
        </div>

        <section className="max-w-4xl mx-auto px-4 md:px-6 pb-16">
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-6 text-center">
            关于火影西瓜
          </h1>
          <p className="text-center text-foreground-500 mb-12 max-w-2xl mx-auto leading-relaxed">
            火影西瓜是一个专注于融合火影忍者主题与西瓜创意的内容平台。
            我们致力于为火影粉丝和创意美食爱好者提供最独特的夏日吃瓜体验。
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 mb-16">
            <div>
              <h2 className="font-heading text-xl font-bold text-foreground-900 mb-4">
                我们的故事
              </h2>
              <p className="text-sm text-foreground-600 leading-relaxed mb-3">
                火影西瓜诞生于一个炎热的夏日午后。创始人——一位资深火影迷——在切西瓜时灵光一闪：
                为什么不把对火影的热爱融入到西瓜创意中呢？
              </p>
              <p className="text-sm text-foreground-600 leading-relaxed mb-3">
                从第一个鸣人西瓜造型开始，我们不断探索火影忍者与西瓜的创意融合。
                从简单的角色造型到复杂的雕刻艺术，从基础切法到主题拼盘，
                我们逐步建立起了一个完整的火影西瓜创意体系。
              </p>
              <p className="text-sm text-foreground-600 leading-relaxed">
                如今，火影西瓜已经成为火影粉丝圈颇具影响力的创意品牌，
                累计服务超过50万火影粉丝，发布了超过200篇创意教程内容。
              </p>
            </div>
            <div className="rounded-lg overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=A%20creative%20studio%20workspace%20with%20multiple%20carved%20watermelons%20featuring%20Naruto%20character%20designs%2C%20wooden%20table%2C%20carving%20tools%2C%20warm%20natural%20light%20from%20window%2C%20artistic%20atmosphere%2C%20Japanese%20decor%20elements&width=800&height=500&seq=about-story-01&orientation=landscape"
                alt="火影西瓜工作室"
                title="火影西瓜创意工作室"
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          <div className="mb-16">
            <h2 className="font-heading text-xl font-bold text-foreground-900 mb-6 text-center">
              我们的使命
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              <div className="bg-background-100 rounded-lg p-6 text-center border border-background-200/70">
                <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 mb-4">
                  <i className="ri-lightbulb-line text-2xl"></i>
                </div>
                <h3 className="font-heading text-base font-bold text-foreground-900 mb-2">
                  创意无限
                </h3>
                <p className="text-xs text-foreground-500 leading-relaxed">
                  持续探索火影忍者与西瓜的创意结合，为粉丝带来前所未有的吃瓜体验
                </p>
              </div>
              <div className="bg-background-100 rounded-lg p-6 text-center border border-background-200/70">
                <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 mb-4">
                  <i className="ri-group-line text-2xl"></i>
                </div>
                <h3 className="font-heading text-base font-bold text-foreground-900 mb-2">
                  社区共创
                </h3>
                <p className="text-xs text-foreground-500 leading-relaxed">
                  连接全球火影粉丝，打造最具活力的火影西瓜创意交流社区
                </p>
              </div>
              <div className="bg-background-100 rounded-lg p-6 text-center border border-background-200/70">
                <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600 mb-4">
                  <i className="ri-heart-line text-2xl"></i>
                </div>
                <h3 className="font-heading text-base font-bold text-foreground-900 mb-2">
                  快乐分享
                </h3>
                <p className="text-xs text-foreground-500 leading-relaxed">
                  让每一颗西瓜都充满欢乐与热度，传递火影忍者的热血精神
                </p>
              </div>
            </div>
          </div>

          <div className="mb-16">
            <h2 className="font-heading text-xl font-bold text-foreground-900 mb-6 text-center">
              用户口碑
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              <div className="bg-background-100 rounded-lg p-5 border border-background-200/70">
                <div className="flex items-center gap-1 mb-3 text-accent-500">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <span key={i} className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-star-fill text-sm"></i>
                    </span>
                  ))}
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed mb-3">
                  跟着教程做了鸣人西瓜，朋友圈直接炸了！火影迷朋友都说绝了，
                  现在每次聚会都要我表演切西瓜。
                </p>
                <p className="text-xs text-foreground-400 font-medium">—— 漩涡小吃货</p>
              </div>
              <div className="bg-background-100 rounded-lg p-5 border border-background-200/70">
                <div className="flex items-center gap-1 mb-3 text-accent-500">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <span key={i} className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-star-fill text-sm"></i>
                    </span>
                  ))}
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed mb-3">
                  写轮眼切法太酷了！我一个厨房小白都能做出来，
                  而且真的有那个味道。强烈推荐给所有火影粉！
                </p>
                <p className="text-xs text-foreground-400 font-medium">—— 宇智波切瓜</p>
              </div>
              <div className="bg-background-100 rounded-lg p-5 border border-background-200/70">
                <div className="flex items-center gap-1 mb-3 text-accent-500">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <span key={i} className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-star-fill text-sm"></i>
                    </span>
                  ))}
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed mb-3">
                  孩子生日派对做了火影西瓜拼盘，所有小朋友都疯了！
                  中忍考试主题太适合互动游戏了，大人也玩得很开心。
                </p>
                <p className="text-xs text-foreground-400 font-medium">—— 木叶奶爸</p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            <div>
              <h2 className="font-heading text-xl font-bold text-foreground-900 mb-4">
                联系我们
              </h2>
              <div className="space-y-3 mb-6">
                <div className="flex items-center gap-3">
                  <span className="w-8 h-8 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                    <i className="ri-mail-line text-sm"></i>
                  </span>
                  <span className="text-sm text-foreground-700">hello@huoyingxigua.com</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                    <i className="ri-wechat-line text-sm"></i>
                  </span>
                  <span className="text-sm text-foreground-700">微信公众号：火影西瓜</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="w-8 h-8 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600">
                    <i className="ri-bilibili-line text-sm"></i>
                  </span>
                  <span className="text-sm text-foreground-700">Bilibili：火影西瓜君</span>
                </div>
              </div>
            </div>

            <div>
              <h2 className="font-heading text-xl font-bold text-foreground-900 mb-4">
                给我们留言
              </h2>
              <form
                id="about-contact-form"
                onSubmit={handleSubmit}
                className="space-y-4"
                data-readdy-form=""
              >
                <input
                  type="text"
                  name="phone_alt"
                  className="form-website-alt-field"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
                <div>
                  <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1">
                    姓名
                  </label>
                  <input
                    id="contact-name"
                    name="name"
                    type="text"
                    required
                    className="w-full px-4 py-2.5 bg-background-50 border border-background-300 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
                    placeholder="你的名字"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1">
                    邮箱
                  </label>
                  <input
                    id="contact-email"
                    name="email"
                    type="email"
                    required
                    className="w-full px-4 py-2.5 bg-background-50 border border-background-300 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
                    placeholder="your@email.com"
                  />
                </div>
                <div>
                  <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1">
                    留言内容
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={4}
                    className="w-full px-4 py-2.5 bg-background-50 border border-background-300 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                    placeholder="写下你想说的话（最多500字）"
                  ></textarea>
                </div>
                <button
                  type="submit"
                  disabled={formStatus === 'loading'}
                  className="px-6 py-2.5 bg-primary-500 text-white rounded-full text-sm font-medium hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer disabled:opacity-60"
                >
                  {formStatus === 'loading' ? '发送中...' : '发送留言'}
                </button>
                {formStatus === 'success' && (
                  <p className="text-sm text-accent-600 font-medium">留言已发送，感谢你的反馈！</p>
                )}
                {formStatus === 'error' && (
                  <p className="text-sm text-red-500 font-medium">{formError}</p>
                )}
              </form>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}